import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import GestureIcon from "@mui/icons-material/Gesture";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import api from "../components/utils/api";
import Logo from "../Img/IRON_Logo blanco sin fondo.svg";

export default function PrimerAcceso() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasFirma, setHasFirma] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const userToken = localStorage.getItem("user-token");
  const nombre = (localStorage.getItem("nombre-Completo") || "").split(" ")[0];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasFirma(true);
  };

  const endDraw = (e) => {
    e.preventDefault();
    setDrawing(false);
  };

  const limpiar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasFirma(false);
  };

  const guardar = async () => {
    if (!hasFirma) return;
    const canvas = canvasRef.current;
    const firma = canvas.toDataURL("image/png");
    setError("");
    try {
      setLoading(true);
      await axios.post(
        api.urlAPI + "auth/firma",
        { firma },
        { headers: { "iron-token": userToken } }
      );
      localStorage.setItem("firma", firma);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Error al guardar la firma.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      bgcolor: "#111318",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
    }}>
      <Box sx={{ mb: 2.5 }}>
        <img src={Logo} alt="Iron Group" height={40} />
      </Box>

      <Box sx={{
        bgcolor: "#1c1f27",
        border: "1px solid #2a2d36",
        borderRadius: 3,
        p: 3,
        width: "100%",
        maxWidth: 420,
      }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
          <GestureIcon sx={{ color: "#ED9519", fontSize: 24 }} />
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#e8e9ef" }}>
              ¡Bienvenido{nombre ? `, ${nombre}` : ""}!
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#888" }}>
              Antes de continuar, registrá tu firma
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: 12, color: "#666", mb: 2, lineHeight: 1.5 }}>
          Tu firma se adjuntará automáticamente en los servicios que crees.
          Firmá en el recuadro de abajo.
        </Typography>

        {/* Canvas */}
        <Box sx={{
          border: "1.5px dashed #2a2d36",
          borderRadius: 2,
          overflow: "hidden",
          mb: 1.5,
          touchAction: "none",
        }}>
          <canvas
            ref={canvasRef}
            width={370}
            height={160}
            style={{ width: "100%", height: "auto", display: "block", cursor: "crosshair" }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
        </Box>

        {/* Limpiar */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={limpiar}
            sx={{ color: "#888", fontSize: 12, textTransform: "none" }}
          >
            Limpiar
          </Button>
        </Box>

        {error && (
          <Typography sx={{ fontSize: 12, color: "#ef4444", mb: 1.5 }}>
            {error}
          </Typography>
        )}

        {/* Guardar */}
        <Button
          fullWidth
          onClick={guardar}
          disabled={!hasFirma || loading}
          sx={{
            bgcolor: hasFirma ? "#ED9519" : "#2a2d36",
            color: hasFirma ? "#1a0e00" : "#555",
            fontWeight: 600,
            borderRadius: 2,
            py: 1.2,
            fontSize: 14,
            "&:hover": { bgcolor: hasFirma ? "#d4851a" : "#2a2d36" },
            transition: "background 0.2s",
          }}
          startIcon={loading ? null : hasFirma ? <CheckIcon /> : null}
        >
          {loading
            ? <CircularProgress size={20} sx={{ color: "#1a0e00" }} />
            : "Guardar firma y continuar"}
        </Button>
      </Box>

      <Typography sx={{ fontSize: 11, color: "#333", mt: 3 }}>
        Iron Group S.A. · {new Date().getFullYear()}
      </Typography>
    </Box>
  );
}
